import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Gavel } from '@mui/icons-material';

import { type OpinionResult, type CourtOpinion } from '../../types';
import { cleanOpinionType } from '../../utils';
import {
  CardContainer,
  ContentContainer,
  HeaderRow,
  CardIconContainer,
  CardTitle,
  CourtCitation,
  DetailsContainer,
  DetailRow,
  DetailLabel,
  DetailValue,
  MoreCount,
  ViewButton,
  OpinionSection,
  OpinionTypeLabel,
  OpinionSnippet,
  formatDate,
  getLimitedList,
  formatCitations,
} from '../shared';

const MAX_DISPLAY_ITEMS = 3;

export interface OpinionsResultCardProps {
  opinionItem: OpinionResult;
  isHovered?: boolean;
}

export const OpinionsResultCard = ({
  opinionItem,
  isHovered,
}: OpinionsResultCardProps) => {
  const { links } = useAppConstantsContext();
  const {
    court_citation_string,
    dateFiled,
    docketNumber,
    court,
    judge,
    citation,
    citeCount,
    opinions,
  } = opinionItem;

  const courtCitation =
    court_citation_string && dateFiled
      ? `(${court_citation_string} ${new Date(dateFiled).getFullYear()})`
      : court_citation_string
      ? `(${court_citation_string})`
      : '';

  const filedDate = dateFiled ? formatDate(dateFiled) : '';
  const citationsText = formatCitations(
    citation?.filter((c): c is string => typeof c === 'string')
  );
  const panelNames = getLimitedList(opinionItem.panel_names, MAX_DISPLAY_ITEMS);

  const firstOpinion: CourtOpinion | undefined = opinions?.[0];
  const opinionType = firstOpinion ? cleanOpinionType(firstOpinion.type) : '';
  const opinionSnippet = firstOpinion?.snippet || '';

  const handleViewClick = () => {
    const url = new URL(
      `${links.baseUrl}/dashboard/court-cases/opinions/${opinionItem.cluster_id}`
    );
    if (firstOpinion?.author_id) {
      url.searchParams.set('assigned_to_id', firstOpinion.author_id.toString());
    }
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  return (
    <CardContainer isHovered={isHovered} hoverColor="grey50">
      <ContentContainer>
        <HeaderRow>
          <CardIconContainer size={44} mobileSize={24}>
            <Gavel />
          </CardIconContainer>
          <CardTitle>{opinionItem.caseName}</CardTitle>
          {courtCitation && <CourtCitation>{courtCitation}</CourtCitation>}
        </HeaderRow>

        <DetailsContainer>
          {filedDate && (
            <DetailRow>
              <DetailLabel>Filed:</DetailLabel>
              <DetailValue>{filedDate}</DetailValue>
            </DetailRow>
          )}

          {court && (
            <DetailRow>
              <DetailLabel>Court:</DetailLabel>
              <DetailValue>{court}</DetailValue>
            </DetailRow>
          )}

          {docketNumber && (
            <DetailRow>
              <DetailLabel>Docket:</DetailLabel>
              <DetailValue>{docketNumber}</DetailValue>
            </DetailRow>
          )}

          {citationsText && (
            <DetailRow>
              <DetailLabel>Citations:</DetailLabel>
              <DetailValue>{citationsText}</DetailValue>
            </DetailRow>
          )}

          {citeCount !== undefined && citeCount > 0 && (
            <DetailRow>
              <DetailLabel>Cited By:</DetailLabel>
              <DetailValue>
                {citeCount} {citeCount === 1 ? 'case' : 'cases'}
              </DetailValue>
            </DetailRow>
          )}

          {judge && (
            <DetailRow>
              <DetailLabel>Judge:</DetailLabel>
              <DetailValue>{judge}</DetailValue>
            </DetailRow>
          )}

          {panelNames && (
            <DetailRow>
              <DetailLabel>Panel:</DetailLabel>
              <DetailValue>
                {panelNames.text}
                {panelNames.remaining > 0 && (
                  <MoreCount> +{panelNames.remaining} more</MoreCount>
                )}
              </DetailValue>
            </DetailRow>
          )}

          {opinionItem.suitNature && (
            <DetailRow>
              <DetailLabel>Nature:</DetailLabel>
              <DetailValue>{opinionItem.suitNature}</DetailValue>
            </DetailRow>
          )}

          {opinionType && (
            <OpinionSection>
              <OpinionTypeLabel>{opinionType}</OpinionTypeLabel>
              {opinionSnippet && (
                <OpinionSnippet>{opinionSnippet}</OpinionSnippet>
              )}
            </OpinionSection>
          )}
        </DetailsContainer>
      </ContentContainer>

      <ViewButton variant="outlined" onClick={handleViewClick}>
        View
      </ViewButton>
    </CardContainer>
  );
};
